import React from 'react'
import Modal from 'components/Luxkit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useCallback } from 'react'
import Group from 'components/utils/Group'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import Label from 'components/Luxkit/Label'
import styled from 'styled-components'
import { rem } from 'polished'
import HelpPane from '../HelpPane'
import Image from 'components/Common/Image/Image'
import FormatCurrency from '../FormatCurrency'
import { capitalise } from 'lib/string/stringUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import UpsellModalCountdown from './UpsellModalCountdown'
import Caption from 'components/Luxkit/Typography/Caption'

const OfferEndsContainer = styled.div`
  margin-top: ${rem(12)};
`

interface Props {
  carrierLogo?: string;
  carrierName: string;
  travelClass?: string;
  nights: number;
  price: number;
  destinationName: string;
  travelPeriods: Array<App.FlightDealTravelPeriod>;
  salesEndDate: string;
}

function UpsellModal(props: Props) {
  const {
    carrierLogo,
    carrierName,
    travelClass,
    nights,
    price,
    destinationName,
    travelPeriods,
    salesEndDate,
  } = props
  const dateFormatter = useIntlDateFormatter()
  const modalElement = useModalElementContext<boolean>()

  const sayYes = useCallback(() => {
    modalElement.resolve(true)
  }, [modalElement])

  return (
    <Modal
      title=""
      primaryActionText="View hotel + flight availability"
      onPrimaryActionClick={sayYes}
    >
      <Group direction="vertical" horizontalAlign="center" gap="12 0">
        {carrierLogo && (
          <Image width={140} image={{ url: carrierLogo, title: carrierName }} />
        )}

        <HeadingTextBlock variant="heading3" align="center">
          {nights} nights + return flights to {destinationName} from{' '}
          <FormatCurrency value={price} format="roundedDollar" /> for 2 adults
          with {carrierName}*
        </HeadingTextBlock>

        <Group direction="vertical" gap="12 0" horizontalAlign="center">
          <BodyTextBlock variant="medium">
            Class: {capitalise(travelClass)}
          </BodyTextBlock>

          <Group direction="vertical" gap="4 0" horizontalAlign="center">
            <CaptionTextBlock variant="small" weight="bold">
              Travel between
            </CaptionTextBlock>

            {travelPeriods.map((period, index) => (
              <BodyTextBlock key={index} variant="medium">
                {dateFormatter(
                  period.departureDate,
                  INTL_DMY_CASUAL_SHORT_FORMAT,
                )}{' '}
                and{' '}
                {dateFormatter(
                  period.arrivalDate,
                  INTL_DMY_CASUAL_SHORT_FORMAT,
                )}
              </BodyTextBlock>
            ))}
          </Group>
        </Group>

        <OfferEndsContainer>
          <Label variant="urgency" kind="primary">
            <Caption variant="small">
              Offer ends in <UpsellModalCountdown salesEndDate={salesEndDate} />
            </Caption>
          </Label>
        </OfferEndsContainer>

        <HelpPane
          heading="Call us to find out more or book this offer today"
          waiverMessage="Fares subject to availability"
        />
      </Group>
    </Modal>
  )
}

export default UpsellModal
